/*
  pf-favorites.js — JD1 local favorites system v2 (single source of truth)
  -------------------------------------------------------------------------
  Storage: localStorage key "pf-favorites" = JSON array of product records:
    { id, handle, title, url, image, price, addedAt }   (addedAt = ms epoch)
  Public API: window.PFFavorites { getAll, count, has, add, remove, toggle, clear }
  Event: document dispatches "pf-favorites:update" { detail: { favorites, count } }

  Hooks (declarative, via data-attributes — no framework dependency):
    [data-pf-fav-toggle]  toggle button; reads data-pf-fav-{id,handle,title,url,image,price}
    [data-pf-fav-open]    opens the favorites drawer
    [data-pf-fav-close]   closes the favorites drawer
    [data-pf-fav-remove]  removes one item (reads data-pf-fav-{handle,id})
    [data-pf-fav-copy]    copies data-code to clipboard (coupon reveal)
    [data-pf-fav-count]   element kept in sync with the count
    [data-pf-fav-bubble]  wrapper toggled .hidden when count === 0
    #pf-fav-drawer        the <dialog> (markup shipped by pf-fav-drawer.liquid)

  Coupon reveal (read from #pf-fav-drawer dataset):
    data-coupon-days   threshold in days (0/absent = coupon off)
    data-coupon-code   discount code to reveal (created by you in Admin)
    data-coupon-text   message; supports {days} and {code} placeholders
    data-copied-text   feedback shown after copying
    data-remove-label / data-empty-text  UI strings
*/
(function () {
  'use strict';

  // Idempotency: this asset may be referenced from several snippets/blocks
  // (drawer + PDP button). Run once to avoid double-binding listeners.
  if (window.__pfFavoritesLoaded) return;
  window.__pfFavoritesLoaded = true;

  var KEY = 'pf-favorites';
  var EVT = 'pf-favorites:update';
  var DAY = 86400000;

  /* ---------- store ---------- */
  function normalize(item) {
    if (item == null) return null;
    if (typeof item === 'string') {
      var isId = /^\d+$/.test(item);
      if (isId) return { id: item, handle: '', title: '', url: '', image: '', price: '', variantId: '', addedAt: Date.now() };
      return { id: '', handle: item, title: item, url: '/products/' + item, image: '', price: '', variantId: '', addedAt: Date.now() };
    }
    if (typeof item === 'object') {
      var handle = item.handle || '';
      var added = parseInt(item.addedAt, 10);
      if (isNaN(added) || added <= 0) added = Date.now();
      return {
        id: item.id != null ? String(item.id) : '',
        handle: handle,
        title: item.title || handle || '',
        url: item.url || (handle ? '/products/' + handle : ''),
        image: item.image || '',
        price: item.price || '',
        variantId: item.variantId != null ? String(item.variantId) : '',
        addedAt: added
      };
    }
    return null;
  }

  function readRaw() {
    var raw;
    try { raw = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { raw = []; }
    return Array.isArray(raw) ? raw : [];
  }

  function read() {
    var raw = readRaw();
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var rec = normalize(raw[i]);
      if (rec && (rec.handle || rec.id)) out.push(rec);
    }
    return out;
  }

  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    document.dispatchEvent(new CustomEvent(EVT, { detail: { favorites: list, count: list.length } }));
  }

  /* migrate legacy/partial data once (string entries, missing addedAt) */
  function migrate() {
    var raw = readRaw();
    var needs = raw.some(function (it) {
      return typeof it === 'string' || it == null ||
        (typeof it === 'object' && (it.addedAt == null || isNaN(parseInt(it.addedAt, 10))));
    });
    if (needs) write(read());
  }

  function sameItem(a, b) {
    if (a.handle && b.handle) return a.handle === b.handle;
    if (a.id && b.id) return a.id === b.id;
    return false;
  }

  var API = {
    getAll: function () { return read(); },
    count: function () { return read().length; },
    has: function (rec) {
      var r = normalize(rec) || {};
      return read().some(function (x) { return sameItem(x, r); });
    },
    add: function (rec) {
      var r = normalize(rec);
      if (!r || (!r.handle && !r.id)) return read();
      var list = read();
      if (!list.some(function (x) { return sameItem(x, r); })) {
        r.addedAt = Date.now();
        list.push(r);
      }
      write(list);
      return list;
    },
    remove: function (rec) {
      var r = normalize(rec) || {};
      var list = read().filter(function (x) { return !sameItem(x, r); });
      write(list);
      return list;
    },
    toggle: function (rec) { return API.has(rec) ? API.remove(rec) : API.add(rec); },
    clear: function () { write([]); }
  };
  window.PFFavorites = API;

  /* ---------- helpers ---------- */
  function recordFromEl(el) {
    var d = el.dataset;
    return {
      id: d.pfFavId || '',
      handle: d.pfFavHandle || '',
      title: d.pfFavTitle || '',
      url: d.pfFavUrl || '',
      image: d.pfFavImage || '',
      price: d.pfFavPrice || '',
      variantId: d.pfFavVariant || ''
    };
  }

  function drawerEl() { return document.getElementById('pf-fav-drawer'); }

  function toastOnAdd() {
    var d = drawerEl();
    if (!d) return false;
    var v = d.dataset.toastOnAdd;
    return v !== '0' && v !== 'false';
  }

  var pfToastTimer = null;
  function toastEl() { return document.getElementById('pf-fav-toast'); }

  function showToast(rec) {
    var t = toastEl();
    if (!t) return;
    var icon = t.querySelector('.pf-fav-toast__icon');
    if (icon && !icon.dataset.filled) {
      var tpl = document.getElementById('pf-fav-ico-tpl');
      if (tpl && tpl.content) { icon.appendChild(tpl.content.cloneNode(true)); icon.dataset.filled = '1'; }
    }
    var msgEl = t.querySelector('.pf-fav-toast__msg');
    if (msgEl) {
      var base = txt('toastText', 'se agregó a tu lista');
      var title = rec && rec.title ? rec.title : '';
      msgEl.textContent = title ? ('«' + title + '» ' + base) : (base.charAt(0).toUpperCase() + base.slice(1));
    }
    var action = t.querySelector('.pf-fav-toast__action');
    if (action) {
      var label = txt('toastAction', '');
      if (label) { action.textContent = label; action.hidden = false; }
      else { action.hidden = true; }
    }
    t.hidden = false;
    void t.offsetWidth; // reflow so the transition runs
    t.classList.add('is-visible');
    if (pfToastTimer) clearTimeout(pfToastTimer);
    pfToastTimer = setTimeout(hideToast, 2800);
  }

  function hideToast() {
    var t = toastEl();
    if (!t) return;
    t.classList.remove('is-visible');
    if (pfToastTimer) { clearTimeout(pfToastTimer); pfToastTimer = null; }
    setTimeout(function () { if (t && !t.classList.contains('is-visible')) t.hidden = true; }, 260);
  }

  function txt(name, fallback) {
    var d = drawerEl();
    return (d && d.dataset[name]) || fallback;
  }

  function couponConfig() {
    var d = drawerEl();
    if (!d) return { days: 0, code: '', text: '', copied: '' };
    var days = parseInt(d.dataset.couponDays, 10);
    return {
      days: isNaN(days) ? 0 : days,
      code: d.dataset.couponCode || '',
      text: d.dataset.couponText || '¡Llevas más de [days] días con esto guardado! Usa [code].',
      copied: d.dataset.copiedText || '¡Copiado!'
    };
  }

  function humanTimeEs(ms) {
    var days = Math.floor(ms / DAY);
    if (days <= 0) return 'hoy';
    if (days === 1) return 'hace 1 día';
    if (days < 7) return 'hace ' + days + ' días';
    var weeks = Math.floor(days / 7);
    if (weeks === 1) return 'hace 1 semana';
    if (weeks < 5) return 'hace ' + weeks + ' semanas';
    var months = Math.floor(days / 30);
    return months <= 1 ? 'hace 1 mes' : 'hace ' + months + ' meses';
  }

  /* ---------- toggle buttons ---------- */
  function syncButton(btn) {
    var active = API.has(recordFromEl(btn));
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var on = btn.getAttribute('data-pf-label-on');
    var off = btn.getAttribute('data-pf-label-off');
    if (on && off) btn.setAttribute('aria-label', active ? on : off);
  }

  function syncAllButtons(root) {
    var nodes = (root || document).querySelectorAll('[data-pf-fav-toggle]');
    for (var i = 0; i < nodes.length; i++) syncButton(nodes[i]);
  }

  /* ---------- counts ---------- */
  function syncCounts(count) {
    var n = count != null ? count : API.count();
    var label = n > 99 ? '99+' : String(n);
    var nodes = document.querySelectorAll('[data-pf-fav-count]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = label;
      nodes[i].classList.toggle('is-empty', n === 0);
      var wrap = nodes[i].closest('[data-pf-fav-bubble]');
      if (wrap) wrap.classList.toggle('hidden', n === 0);
    }
  }

  /* ---------- drawer ---------- */
  function svgHeartOutline() {
    return '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" ' +
      'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
  }

  function emptyState() {
    var wrap = document.createElement('div');
    wrap.className = 'pf-fav-empty';
    var icon = document.createElement('span');
    icon.className = 'pf-fav-empty__icon';
    icon.setAttribute('aria-hidden', 'true');
    var tpl = document.getElementById('pf-fav-ico-tpl');
    if (tpl && tpl.content) icon.appendChild(tpl.content.cloneNode(true));
    else icon.innerHTML = svgHeartOutline();
    wrap.appendChild(icon);
    var p = document.createElement('p');
    p.className = 'pf-fav-empty__text';
    p.textContent = txt('emptyText', 'Aún no tienes favoritos.');
    wrap.appendChild(p);
    return wrap;
  }

  function couponRow(code, message) {
    var row = document.createElement('div');
    row.className = 'pf-fav-item__coupon';
    var msg = document.createElement('span');
    msg.className = 'pf-fav-item__coupon-text';
    msg.textContent = message;
    row.appendChild(msg);
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pf-fav-item__coupon-code button-unstyled';
    btn.setAttribute('data-pf-fav-copy', '');
    btn.dataset.code = code;
    btn.textContent = code;
    row.appendChild(btn);
    return row;
  }

  function itemEl(rec) {
    var cfg = couponConfig();
    var elapsed = Date.now() - rec.addedAt;
    var eligible = cfg.days > 0 && cfg.code && elapsed >= cfg.days * DAY;

    var li = document.createElement('li');
    li.className = 'pf-fav-item' + (eligible ? ' pf-fav-item--coupon' : '');

    var main = document.createElement('div');
    main.className = 'pf-fav-item__main';

    var a = document.createElement('a');
    a.className = 'pf-fav-item__link';
    a.href = rec.url || (rec.handle ? '/products/' + rec.handle : '#');

    if (rec.image) {
      var img = document.createElement('img');
      img.className = 'pf-fav-item__img';
      img.src = rec.image;
      img.alt = rec.title || '';
      img.loading = 'lazy';
      a.appendChild(img);
    } else {
      var ph = document.createElement('span');
      ph.className = 'pf-fav-item__img pf-fav-item__img--placeholder';
      a.appendChild(ph);
    }

    var info = document.createElement('span');
    info.className = 'pf-fav-item__info';
    var t = document.createElement('span');
    t.className = 'pf-fav-item__title';
    t.textContent = rec.title || rec.handle || '';
    info.appendChild(t);
    if (rec.price) {
      var pr = document.createElement('span');
      pr.className = 'pf-fav-item__price';
      pr.textContent = rec.price;
      info.appendChild(pr);
    }
    var tm = document.createElement('span');
    tm.className = 'pf-fav-item__time';
    tm.textContent = humanTimeEs(elapsed);
    info.appendChild(tm);
    a.appendChild(info);
    main.appendChild(a);

    var rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'pf-fav-item__remove button-unstyled';
    rm.setAttribute('data-pf-fav-remove', '');
    rm.setAttribute('aria-label', txt('removeLabel', 'Quitar de favoritos'));
    if (rec.handle) rm.dataset.pfFavHandle = rec.handle;
    if (rec.id) rm.dataset.pfFavId = rec.id;
    rm.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true">' +
      '<path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>';
    main.appendChild(rm);

    li.appendChild(main);

    if (rec.variantId) {
      var actions = document.createElement('div');
      actions.className = 'pf-fav-item__actions';
      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'pf-fav-item__add';
      add.setAttribute('data-pf-fav-add', '');
      add.dataset.variant = rec.variantId;
      add.textContent = txt('addLabel', 'Agregar al carrito');
      actions.appendChild(add);
      li.appendChild(actions);
    }

    if (eligible) {
      var message = cfg.text
        .split('[days]').join(cfg.days).split('{days}').join(cfg.days)
        .split('[code]').join(cfg.code).split('{code}').join(cfg.code);
      li.appendChild(couponRow(cfg.code, message));
    }

    return li;
  }

  function renderDrawer() {
    var list = API.getAll();
    var countEl = document.getElementById('pf-fav-drawer-count');
    if (countEl) countEl.textContent = list.length > 99 ? '99+' : String(list.length);
    var body = document.getElementById('pf-fav-drawer-body');
    if (!body) return;
    body.innerHTML = '';
    if (!list.length) { body.appendChild(emptyState()); return; }
    var ul = document.createElement('ul');
    ul.className = 'pf-fav-list';
    for (var i = 0; i < list.length; i++) ul.appendChild(itemEl(list[i]));
    body.appendChild(ul);
  }

  function openDrawer() {
    var d = drawerEl();
    if (!d) return;
    hideToast();
    renderDrawer();
    if (typeof d.showModal === 'function') { if (!d.open) d.showModal(); }
    else d.setAttribute('open', '');
    document.documentElement.classList.add('pf-fav-scroll-lock');
  }

  function closeDrawer() {
    var d = drawerEl();
    if (!d) return;
    if (typeof d.close === 'function') { if (d.open) d.close(); }
    else d.removeAttribute('open');
    document.documentElement.classList.remove('pf-fav-scroll-lock');
  }

  function addToCart(btn) {
    var variant = btn.dataset.variant;
    if (!variant || btn.dataset.busy) return;
    btn.dataset.busy = '1';
    var original = btn.textContent;
    btn.classList.add('is-loading');
    btn.disabled = true;
    btn.textContent = txt('addingLabel', 'Agregando…');
    var reset = function (label, keepDone) {
      btn.classList.remove('is-loading');
      btn.disabled = false;
      btn.textContent = label;
      setTimeout(function () {
        btn.textContent = original;
        btn.classList.remove('is-done');
        delete btn.dataset.busy;
      }, 1800);
      if (keepDone) btn.classList.add('is-done');
    };
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variant, 10), quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function () {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        // Horizon's cart-icon listens to 'cart:update'; source 'product-form-component' makes it add the delta.
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, source: 'pf-favorites', data: { source: 'product-form-component', itemCount: 1 } }
        }));
        reset(txt('addedLabel', '¡Agregado! ✓'), true);
      })
      .catch(function () { reset(txt('addErrorLabel', 'Error, reintenta'), false); });
  }

  function copyCode(btn) {
    var code = btn.dataset.code || '';
    if (!code) return;
    var done = function () {
      var prev = btn.textContent;
      btn.classList.add('is-copied');
      btn.textContent = couponConfig().copied;
      setTimeout(function () { btn.textContent = prev; btn.classList.remove('is-copied'); }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(done);
    } else {
      try {
        var ta = document.createElement('textarea');
        ta.value = code; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (e) {}
      done();
    }
  }

  /* ---------- events ---------- */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-pf-fav-toggle]');
    if (toggle) {
      e.preventDefault();
      var rec = recordFromEl(toggle);
      var wasActive = API.has(rec);
      API.toggle(rec);
      if (!wasActive && toastOnAdd()) showToast(rec);
      return;
    }
    var open = e.target.closest('[data-pf-fav-open]');
    if (open) { e.preventDefault(); openDrawer(); return; }
    var close = e.target.closest('[data-pf-fav-close]');
    if (close) { e.preventDefault(); closeDrawer(); return; }
    var copy = e.target.closest('[data-pf-fav-copy]');
    if (copy) { e.preventDefault(); copyCode(copy); return; }
    var addBtn = e.target.closest('[data-pf-fav-add]');
    if (addBtn) { e.preventDefault(); addToCart(addBtn); return; }
    var remove = e.target.closest('[data-pf-fav-remove]');
    if (remove) {
      e.preventDefault();
      API.remove({ handle: remove.dataset.pfFavHandle || '', id: remove.dataset.pfFavId || '' });
      return;
    }
  });

  document.addEventListener(EVT, function (e) {
    syncCounts(e.detail ? e.detail.count : null);
    syncAllButtons();
    var d = drawerEl();
    if (d && d.open) renderDrawer();
  });

  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { syncCounts(); syncAllButtons(); var d = drawerEl(); if (d && d.open) renderDrawer(); }
  });

  function bindDrawer() {
    var d = drawerEl();
    if (!d || d.__pfBound) return;
    d.__pfBound = true;
    d.addEventListener('click', function (e) { if (e.target === d) closeDrawer(); });
    d.addEventListener('close', function () { document.documentElement.classList.remove('pf-fav-scroll-lock'); });
  }

  function init() {
    migrate();
    bindDrawer();
    syncCounts();
    syncAllButtons();
    if ('MutationObserver' in window && document.body) {
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches('[data-pf-fav-toggle]')) syncButton(node);
            if (node.querySelectorAll) {
              var inner = node.querySelectorAll('[data-pf-fav-toggle]');
              for (var k = 0; k < inner.length; k++) syncButton(inner[k]);
            }
            if (node.id === 'pf-fav-drawer' || (node.querySelector && node.querySelector('#pf-fav-drawer'))) bindDrawer();
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function () { bindDrawer(); syncCounts(); syncAllButtons(); });
})();